import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Plus,
  Send,
  X,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  emailReceiptsService,
  type EmailReceipt,
} from '../../services/emailReceiptsService';
import { inspectionService } from '../../services/inspectionService';
import { sentAtUtc } from './sentAtUtc';

/**
 * What happened to a report's emails, and a way to send it again
 * (JIM-1007-11; Jim 2026-10-07: "How do we see the details of emailed
 * reports? … if they need to send again or to another person?").
 *
 * The list is the inspection's rows in email_sends with the delivery status
 * Resend reported. "Send again" offers the site's current recipients, ticked,
 * plus anyone by address; the API mails the stored PDF and never asks for a
 * signature again. Shared by the Lite site page and the Hub Inspections tab.
 */
export function ReportEmails({
  inspectionId,
  canSend,
}: {
  inspectionId: string;
  canSend: boolean;
}) {
  const qc = useQueryClient();
  const key = ['report-emails', inspectionId];
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () =>
      emailReceiptsService.list({
        inspection_id: inspectionId,
        email_type: 'inspection_report',
        limit: 100,
      }),
  });
  const [composing, setComposing] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  const receipts = data?.items ?? [];

  return (
    <div className="text-sm" data-testid="report-emails">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />
          Emailed to
        </h4>
        {canSend && !composing && (
          <button
            type="button"
            onClick={() => {
              setOutcome(null);
              setComposing(true);
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30"
          >
            <Send className="w-3.5 h-3.5" />
            Send again
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </p>
      ) : isError ? (
        <p className="text-red-600 dark:text-red-400">
          Couldn't load this report's emails.
        </p>
      ) : receipts.length === 0 ? (
        <p className="text-slate-500 dark:text-slate-400">
          Not emailed to anyone yet.
        </p>
      ) : (
        <ul className="space-y-1">
          {receipts.map((r) => (
            <ReceiptRow key={r.id} receipt={r} />
          ))}
        </ul>
      )}

      {outcome && (
        <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">
          {outcome}
        </p>
      )}

      {composing && (
        <SendAgainSheet
          inspectionId={inspectionId}
          onClose={() => setComposing(false)}
          onSent={(summary) => {
            setOutcome(summary);
            setComposing(false);
            qc.invalidateQueries({ queryKey: key });
          }}
        />
      )}
    </div>
  );
}

/** Delivery outcome from Resend's events — what the client actually got. */
function DeliveryBadge({ status }: { status: string }) {
  const good =
    'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800';
  const bad =
    'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800';
  const warn =
    'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800';
  const map: Record<string, { cls: string; Icon: typeof CheckCircle2 }> = {
    delivered: { cls: good, Icon: CheckCircle2 },
    opened: { cls: good, Icon: CheckCircle2 },
    clicked: { cls: good, Icon: CheckCircle2 },
    bounced: { cls: bad, Icon: XCircle },
    'failed to send': { cls: bad, Icon: XCircle },
    'marked as spam': { cls: warn, Icon: AlertTriangle },
    delayed: { cls: warn, Icon: Clock },
  };
  const { cls, Icon } = map[status] ?? {
    cls: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    Icon: Clock,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded border ${cls}`}
    >
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}

function ReceiptRow({ receipt }: { receipt: EmailReceipt }) {
  return (
    <li className="flex items-center gap-2 min-w-0">
      <span className="flex-1 min-w-0 truncate text-slate-800 dark:text-slate-100">
        {receipt.to_email}
      </span>
      <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
        {format(sentAtUtc(receipt.sent_at), 'MMM d, h:mm a')}
      </span>
      <DeliveryBadge status={receipt.delivery_status} />
    </li>
  );
}

const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function SendAgainSheet({
  inspectionId,
  onClose,
  onSent,
}: {
  inspectionId: string;
  onClose: () => void;
  onSent: (summary: string) => void;
}) {
  const { data: who, isLoading } = useQuery({
    queryKey: ['report-recipients', inspectionId],
    queryFn: () => inspectionService.getReportRecipients(inspectionId),
  });
  const siteRecipients = who?.recipients ?? [];
  // Everyone the site sends to starts ticked — "hey, I never got it" is the
  // common case; a single person is one tap away.
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [extras, setExtras] = useState<Array<{ email: string; name: string }>>(
    []
  );
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const chosen = [
    ...siteRecipients
      .filter((r) => !unticked.has(r.email))
      .map((r) => ({ email: r.email, name: r.name })),
    ...extras,
  ];

  const send = useMutation({
    mutationFn: () => inspectionService.sendReportAgain(inspectionId, chosen),
    onSuccess: (res) => {
      const failed = res.recipients.filter((r) => r.status !== 'sent');
      onSent(
        failed.length
          ? `Sent to ${res.sent_count}. Could not send to ${failed
              .map((r) => r.email)
              .join(', ')}.`
          : `Sent to ${res.sent_count} ${res.sent_count === 1 ? 'person' : 'people'}.`
      );
    },
    onError: () =>
      setProblem("Couldn't send the report. Nothing may have gone out."),
  });

  const addExtra = () => {
    const addr = email.trim().toLowerCase();
    if (!EMAIL_OK.test(addr)) {
      setProblem('Enter a full email address.');
      return;
    }
    if (chosen.some((c) => c.email.toLowerCase() === addr)) {
      setProblem('That address is already on the list.');
      return;
    }
    setExtras((xs) => [...xs, { email: addr, name: name.trim() }]);
    setEmail('');
    setName('');
    setProblem(null);
  };

  return (
    <div
      className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 space-y-3"
      role="dialog"
      aria-label="Send the report again"
    >
      <div className="flex items-center justify-between">
        <p className="font-medium text-slate-900 dark:text-white">
          Send this report to
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {isLoading ? (
        <p className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading the site's people…
        </p>
      ) : siteRecipients.length === 0 ? (
        <p className="text-slate-500 dark:text-slate-400">
          This site has nobody set to receive reports. Add an address below.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {siteRecipients.map((r) => (
            <li key={r.email}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!unticked.has(r.email)}
                  onChange={(e) =>
                    setUnticked((s) => {
                      const next = new Set(s);
                      if (e.target.checked) next.delete(r.email);
                      else next.add(r.email);
                      return next;
                    })
                  }
                  className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="min-w-0 truncate text-slate-800 dark:text-slate-100">
                  {r.name && r.name !== r.email ? `${r.name} · ` : ''}
                  <span className="text-slate-500 dark:text-slate-400">
                    {r.email}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {extras.length > 0 && (
        <ul className="space-y-1.5">
          {extras.map((x) => (
            <li key={x.email} className="flex items-center gap-2">
              <span className="flex-1 min-w-0 truncate text-slate-800 dark:text-slate-100">
                {x.name ? `${x.name} · ` : ''}
                <span className="text-slate-500 dark:text-slate-400">
                  {x.email}
                </span>
              </span>
              <button
                type="button"
                onClick={() =>
                  setExtras((xs) => xs.filter((y) => y.email !== x.email))
                }
                aria-label={`Remove ${x.email}`}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
          Someone else
        </p>
        <div className="flex flex-wrap gap-1.5">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email address"
            aria-label="Email address"
            className="flex-1 min-w-[160px] px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="name (optional)"
            aria-label="Name"
            className="flex-1 min-w-[120px] px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
          <button
            type="button"
            onClick={addExtra}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </div>

      {problem && (
        <p className="text-xs text-red-600 dark:text-red-400">{problem}</p>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => send.mutate()}
          disabled={chosen.length === 0 || send.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
        >
          {send.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {send.isPending
            ? 'Sending…'
            : `Send to ${chosen.length} ${chosen.length === 1 ? 'person' : 'people'}`}
        </button>
      </div>
    </div>
  );
}
